"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode, A11y } from "swiper/modules";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import SectionContainer from "@/components/common/SectionContainer";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/free-mode";
import "swiper/css/a11y";
function ParadiseGallerySlider() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="flex gap-4 md:gap-6 overflow-hidden">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] w-[240px] sm:w-[280px] md:w-[340px] shrink-0 rounded-lg bg-muted animate-pulse" />)}
      </div>;
  }
  if (!posts || posts.length === 0) {
    return null;
  }
  return <Swiper modules={[Autoplay, FreeMode, A11y]} loop={true} freeMode={true} slidesPerView="auto" spaceBetween={16} breakpoints={{
    768: {
      spaceBetween: 24
    }
  }} speed={6000} autoplay={{
    delay: 0,
    disableOnInteraction: false,
    pauseOnMouseEnter: true
  }} a11y={{
    prevSlideMessage: "Previous slide",
    nextSlideMessage: "Next slide"
  }} className="w-full">
      {posts.map((post: any, i: number) => {
      const acf = post.acf || post.customFields || {};
      const imageSrc = acf.image || post.featuredImage || "";
      const altText = acf.alt_text || "Gallery image";
      const captionText = acf.caption || "";
      const formattedCaption = captionText ? captionText : "";
      return <SwiperSlide key={post.id || i} data-index={i} className="!w-auto">
            <div className="group relative aspect-[4/5] w-[240px] sm:w-[280px] md:w-[340px] overflow-hidden rounded-lg">
              {imageSrc ? <img src={imageSrc} data-wvc-srcset="" data-wvc-sizes="" alt={altText} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" /> : <div className="absolute inset-0 bg-muted" />}

              <div className="absolute inset-0 bg-primary/5 mix-blend-multiply pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/25 via-transparent to-transparent opacity-70 pointer-events-none" />

              {formattedCaption && <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 opacity-0 translate-y-2 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0">
                  <p className="font-default text-[11px] tracking-[0.25em] uppercase text-background">
                    {formattedCaption}
                  </p>
                </div>}
            </div>
          </SwiperSlide>;
    })}
    </Swiper>;
}
export default function Paradise() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const paragraph = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const galleryReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2871" id="a-glimpse-of-paradise" className="bg-background text-foreground py-10 md:py-14 lg:py-16 overflow-hidden">
      <SectionContainer>
        
        <div className="grid grid-cols-12 gap-6 md:gap-8 mb-10 md:mb-14">
          <div ref={header.ref} className={`col-span-12 md:col-span-7 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default text-xs tracking-[0.25em] uppercase text-muted-foreground mb-5">
              The Venue
            </p>
            <h2 className="font-default font-normal text-foreground text-5xl md:text-7xl lg:text-[72px] leading-[1.05] tracking-tight">
              <span className={`block transition-all duration-1000 delay-100 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                A glimpse of
              </span>
              <span className={`block italic text-primary transition-all duration-1000 delay-300 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                paradise.
              </span>
            </h2>
          </div>

          <div ref={paragraph.ref} className={`col-span-12 md:col-span-4 md:col-start-9 flex md:items-end transition-all duration-1000 delay-500 ease-out ${paragraph.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div>
              <p className="font-default text-[17px] leading-[1.7] text-foreground/85">
                Nestled along the tranquil shores, our venue offers a refined
                backdrop for life's most significant celebrations. Experience
                unparalleled elegance, where architectural beauty meets the
                calm of the water.
              </p>
              <p className="font-default text-xs tracking-[0.2em] uppercase text-muted-foreground mt-6">
                Paradise Cove at Lake Grapevine
              </p>
            </div>
          </div>
        </div>

        <div ref={galleryReveal.ref} className={`transition-all duration-1000 ease-out ${galleryReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <PostsProvider wp_query={{
          post_type: "gallery_image",
          per_page: 20,
          status: ["publish"]
        }}>
            <ParadiseGallerySlider />
          </PostsProvider>
        </div>
      </SectionContainer>
    </section>;
}